import { useCallback, useState, useRef, useEffect } from "react";
import Map, { Source, Layer, NavigationControl } from "react-map-gl";
import {
  mapStyles,
  mdMapConfig,
  paMapConfig,
  vaMapConfig,
  mapStyle,
  mapboxToken,
} from "../../config";
import { filterSchools, normalize } from "../../utils";
import Button from "../Button";
import SchoolPopup from "../SchoolPopup";
import states from "../../data/states";
import * as layers from "../../layers";
import { views, stateCodes } from "../../constants";
import {
  AppFilters,
  MapFiltersButton,
  Legend,
  StateMapButtons,
} from "./styled.js";
import { Wrapper, Container, MapSearch, MapViewToggle } from "../App/styled.js";
import { LegendTitle, LegendSwatch } from "../Legend/styled.js";
import DistrictPopup from "../DistrictPopup";

function StateMap({
  className,
  stateCode,
  stateName,
  schoolsData,
  onViewChange,
  mapView,
  enableS4sGrantees,
  ...props
}) {
  const isMobile = window.matchMedia("(max-width: 640px)").matches;
  let config;
  switch (stateCode) {
    case stateCodes.MD:
      config = mdMapConfig;
      break;
    case stateCodes.PA:
      config = paMapConfig;
      break;
    case stateCodes.VA:
      config = vaMapConfig;
      break;
    default:
      break;
  }
  const mapConfig = isMobile ? { ...config, zoom: config.mobileZoom } : config;

  const [schoolMarker, setSchoolMarker] = useState(
    mapView === views.S4S_GRANTEES ? "school-navy" : "school-blue"
  );
  const [caseStudy, setCaseStudy] = useState("");
  const [activeDistrict, setActiveDistrict] = useState("");
  const [activeHoverDistrict, setActiveHoverDistrict] = useState("");
  const [school, setSchool] = useState(null);
  const [schools, setSchools] = useState(schoolsData);
  const [district, setDistrict] = useState(null);
  const [districts, setDistricts] = useState(null);
  const [stateDistricts, setStateDistricts] = useState(null);
  const [mapViewState, setMapViewState] = useState(mapConfig);
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    systemSize: [],
    funding: [],
    structureType: [],
    caseStudies: [],
    batteryStorage: [],
    cleanEnergyTechnology: [],
  });

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeSchoolsCircle, setActiveSchoolsCircle] = useState("");
  const [activeClusterLayer1, setActiveClusterLayer1] = useState("");
  const [activeClusterLayer2, setActiveClusterLayer2] = useState("");
  const [activeClusterLayer3, setActiveClusterLayer3] = useState("");
  const [activeClusterLayer4, setActiveClusterLayer4] = useState("");
  const mapRef = useRef();

  useEffect(() => {
    setSchoolMarker(
      mapView === views.S4S_GRANTEES ? "school-navy" : "school-blue"
    );
  }, [mapView]);

  useEffect(() => {
    fetch(`/${stateCode.toLowerCase()}-data.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then((json) => {
        setDistricts(json);
        setStateDistricts(json);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, [stateCode]);

  const handleReset = () => {
    setMapViewState(mapConfig);
    setSchool(null);
    setDistrict(null);
    setCaseStudy(null);
    setActiveDistrict("");
  };

  const onMove = useCallback(
    ({ viewState }) => {
      if (viewState.zoom < mapConfig.minZoom) {
        viewState.zoom = mapConfig.minZoom;
      }
      setMapViewState(viewState);
    },
    [mapConfig.minZoom]
  );

  const getPlaceholder = () => {
    return mapView === views.STATE_SCHOOLS || views.S4S_GRANTEES
      ? "School Name, District, City"
      : "School District Name";
  };

  // const paSchools = schools.filter((x) => {
  //   return x.properties.state === "PA";
  // });

  const onClick = useCallback((event) => {
    event.originalEvent.stopPropagation();
    const feature = event.features && event.features[0];

    switch (feature.layer.id) {
      case "schools-circle":
        mapRef.current.flyTo({
          center: [event.lngLat.lng, event.lngLat.lat],
          duration: 1000,
          zoom: 8,
        });
        break;
      case "schools-cluster":
        mapRef.current.flyTo({
          center: [event.lngLat.lng, event.lngLat.lat],
          duration: 1000,
          zoom: 8,
        });
        break;
      case "schools-marker":
        if (!feature.properties.cluster) {
          setSchool(feature.properties);
          setCaseStudy(null);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
            zoom: 8,
          });
        }
        break;
      case "case-studies-marker":
        if (!feature.properties.cluster) {
          setCaseStudy(feature.properties);
          setSchool(null);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
            zoom: 8,
          });
        }
        break;
      case "state-district-hover":
        if (feature.properties.longitude && feature.properties.latitude) {
          setDistrict(feature.properties);
          setActiveDistrict(feature.properties.school_dis);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
          });
        }
        break;
      default:
        break;
    }
  }, []);

  const onHover = useCallback((event) => {
    const feature = event.features && event.features[0];
    if (
      feature?.layer.id === "state-district-fills-capacity" ||
      feature?.layer.id === "state-district-hover"
    ) {
      setActiveHoverDistrict(feature.properties.school_dis);
      mapRef.current.getCanvas().style.cursor = "pointer";
    } else {
      mapRef.current.getCanvas().style.cursor = "";
    }
    if (!feature) {
      setActiveHoverDistrict("");
    }
  }, []);

  const onMouseEnter = useCallback((event) => {
    const feature = event.features && event.features[0];
    switch (feature.layer.id) {
      case "schools-marker":
        break;
      case "case-studies-marker":
        break;
      case "schools-circle":
        setActiveSchoolsCircle(feature.properties.id);
        break;
      default:
        break;
    }
    if (feature?.layer.id === "schools-cluster") {
      const pointCount = feature.properties.point_count;
      if (pointCount <= 50) {
        setActiveClusterLayer1(feature.properties.cluster_id);
      } else if (pointCount <= 99) {
        setActiveClusterLayer2(feature.properties.cluster_id);
      } else if (pointCount <= 249) {
        setActiveClusterLayer3(feature.properties.cluster_id);
      } else {
        setActiveClusterLayer4(feature.properties.cluster_id);
      }
    }
  }, []);

  const onMouseLeave = useCallback((event) => {
    setActiveHoverDistrict("");
    setActiveClusterLayer1("");
    setActiveClusterLayer2("");
    setActiveClusterLayer3("");
    setActiveClusterLayer4("");
  }, []);

  const handleToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const handleFilter = (filters) => {
    setActiveFilters(filters);
  };

  const handleSearch = (searchTerm) => {
    setActiveSearchTerm(searchTerm);
  };

  const handleDistrictClose = () => {
    setDistrict(null);
    setActiveDistrict("");
  };

  useEffect(() => {
    setSchools(filterSchools(activeSearchTerm, activeFilters, schoolsData));
    setSchool(null);
    setCaseStudy(null);
  }, [activeSearchTerm, activeFilters, schoolsData]);

  const handleDistrictSearch = (searchTerm) => {
    if (searchTerm === "") {
      setDistricts(stateDistricts);
      return;
    }
    const filtered = stateDistricts.features.filter(function (x) {
      var district = normalize(x.properties.school_dis);
      return district.indexOf(searchTerm) > -1;
    });
    setDistricts({
      type: "FeatureCollection",
      features: filtered,
    });
  };

  const handleMapView = (view) => {
    onViewChange(view);
  };

  return (
    <>
      {mapView === views.STATE_SCHOOLS && (
        <MapFiltersButton
          activeFilters={activeFilters}
          results={schools}
          onClick={handleToggle}
        />
      )}
      <MapViewToggle>
        <Button
          type="light"
          isActive={mapView === views.STATE_SCHOOLS}
          onClick={() => handleMapView(views.STATE_SCHOOLS)}
        >
          School
        </Button>
        <Button
          type="light"
          isActive={mapView === views.STATE_DISTRICTS}
          onClick={() => handleMapView(views.STATE_DISTRICTS)}
        >
          School District
        </Button>
        {enableS4sGrantees && (
          <Button
            type="light"
            isActive={mapView === views.S4S_GRANTEES}
            onClick={() => handleMapView(views.S4S_GRANTEES)}
          >
            Solar For Schools Grant Program
          </Button>
        )}
      </MapViewToggle>
      <Wrapper>
        <MapSearch
          placeholder={getPlaceholder()}
          onSearch={(searchTerm) => {
            mapView === views.STATE_SCHOOLS || mapView === views.S4S_GRANTEES
              ? handleSearch(searchTerm)
              : handleDistrictSearch(searchTerm);
          }}
        />
        <Container>
          {mapView === views.STATE_SCHOOLS && (
            <AppFilters
              activeFilters={activeFilters}
              results={schools}
              isOpen={filtersOpen}
              onToggle={handleToggle}
              onFilter={handleFilter}
            />
          )}
          {mapView === views.STATE_DISTRICTS && (
            <Legend>
              <LegendTitle>Solar Capacity</LegendTitle>
              <ul>
                <li>
                  <LegendSwatch
                    color={mapStyles.legendDistrictTier1Color}
                  ></LegendSwatch>{" "}
                  {`0 kW`}
                </li>
                <li>
                  <LegendSwatch
                    color={mapStyles.legendTier2Color}
                  ></LegendSwatch>
                  1-50 kW
                </li>
                <li>
                  <LegendSwatch
                    color={mapStyles.legendTier3Color}
                  ></LegendSwatch>
                  51-249 kW
                </li>
                <li>
                  <LegendSwatch
                    color={mapStyles.legendTier4Color}
                  ></LegendSwatch>
                  250-499 kW
                </li>
                <li>
                  <LegendSwatch
                    color={mapStyles.legendTier5Color}
                  ></LegendSwatch>
                  500-999 kW
                </li>
                <li>
                  <LegendSwatch
                    color={mapStyles.legendTier6Color}
                  ></LegendSwatch>
                  1000+ kW
                </li>
              </ul>
            </Legend>
          )}
          {(mapView === views.STATE_SCHOOLS ||
            mapView === views.S4S_GRANTEES) && (
            <Map
              ref={mapRef}
              {...mapViewState}
              id="schoolsMap"
              onMove={onMove}
              width="100%"
              height="100%"
              mapStyle={mapStyle}
              mapboxAccessToken={mapboxToken}
              onClick={onClick}
              onMouseMove={onHover}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              interactiveLayerIds={[
                "schools-marker",
                "case-studies-marker",
                "schools-circle",
                "schools-cluster",
              ]}
            >
              <NavigationControl showCompass={false} />
              <Source id="states" type="geojson" data={states}>
                <Layer
                  id="state-fills"
                  {...layers.stateFillsNumberSolarSchools}
                  paint={{ "fill-color": "#fff", "fill-opacity": 1 }}
                  filter={["!=", ["get", "name"], stateName]}
                />
                <Layer id="state-borders" {...layers.stateBordersStatesLayer} />
              </Source>
              <Source id="states" type="geojson" data={states}>
                <Layer
                  id="state-borders"
                  filter={["==", "name", stateName]}
                  {...layers.stateBorderLayer}
                />
              </Source>
              <Source
                id="schools-data"
                type="geojson"
                data={{ type: "FeatureCollection", features: schools }}
                cluster={true}
                clusterMaxZoom={6}
                clusterRadius={50}
              >
                <Layer
                  {...layers.schoolsMarkerLayer}
                  layout={{
                    ...layers.schoolsMarkerLayer.layout,
                    "icon-image": schoolMarker,
                  }}
                />
                <Layer {...layers.caseStudiesMarkerLayer} />
                <Layer {...layers.schoolsCircleLayer} />
                <Layer
                  {...layers.schoolsCircleHoverLayer}
                  filter={["==", "id", activeSchoolsCircle]}
                />
                <Layer {...layers.schoolsClusterLayer} />
                <Layer
                  {...layers.clustersHoverLayer1}
                  filter={["==", "cluster_id", activeClusterLayer1]}
                />
                <Layer
                  {...layers.clustersHoverLayer2}
                  filter={["==", "cluster_id", activeClusterLayer2]}
                />
                <Layer
                  {...layers.clustersHoverLayer3}
                  filter={["==", "cluster_id", activeClusterLayer3]}
                />
                <Layer
                  {...layers.clustersHoverLayer4}
                  filter={["==", "cluster_id", activeClusterLayer4]}
                />
                <Layer {...layers.schoolsClusterCountLayer} />
              </Source>
              {caseStudy && (
                <SchoolPopup
                  anchor="bottom"
                  longitude={Number(caseStudy.longitude)}
                  latitude={Number(caseStudy.latitude)}
                  onClose={() => setCaseStudy(null)}
                  closeOnClick={false}
                  data={caseStudy}
                  mapView={mapView}
                ></SchoolPopup>
              )}
              {school && (
                <SchoolPopup
                  anchor="bottom"
                  longitude={Number(school.longitude)}
                  latitude={Number(school.latitude)}
                  onClose={() => setSchool(null)}
                  closeOnClick={false}
                  data={school}
                  mapView={mapView}
                ></SchoolPopup>
              )}
            </Map>
          )}
          {mapView === views.STATE_DISTRICTS && (
            <Map
              ref={mapRef}
              {...mapViewState}
              id="statesMap"
              onMove={onMove}
              width="100%"
              height="100%"
              mapStyle={mapStyle}
              mapboxAccessToken={mapboxToken}
              onClick={onClick}
              onMouseMove={onHover}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              interactiveLayerIds={[
                "state-district-fills-capacity",
                "state-district-hover",
                "state-district-borders",
              ]}
            >
              <NavigationControl showCompass={false} />
              <Source id="states" type="geojson" data={states}>
                <Layer
                  id="state-fills"
                  {...layers.stateFillsNumberSolarSchools}
                  paint={{ "fill-color": "#fff", "fill-opacity": 1 }}
                />
                <Layer id="state-borders" {...layers.stateBordersStatesLayer} />
              </Source>
              <Source
                id="all-district-data"
                type="geojson"
                data={stateDistricts}
              >
                <Layer {...layers.stateDistrictFills} />
                <Layer {...layers.stateAllDistrictBorders} />
              </Source>
              <Source id="district-data" type="geojson" data={districts}>
                <Layer
                  {...layers.stateDistrictFillsCapacity}
                  layout={{
                    visibility:
                      mapView === views.STATE_DISTRICTS ? "visible" : "none",
                  }}
                />
                <Layer {...layers.stateDistrictBorders} />
                <Layer
                  id="state-district-hover"
                  type="fill"
                  paint={{ "fill-color": "#fff", "fill-opacity": 0.2 }}
                  filter={["==", "school_dis", activeHoverDistrict]}
                />
                <Layer
                  {...layers.stateActiveDistrictBorder}
                  filter={["==", "school_dis", activeDistrict]}
                />
              </Source>
              {district && (
                <DistrictPopup
                  anchor="bottom"
                  longitude={Number(district.longitude)}
                  latitude={Number(district.latitude)}
                  onClose={handleDistrictClose}
                  closeOnClick={false}
                  data={district}
                ></DistrictPopup>
              )}
            </Map>
          )}
          <StateMapButtons
            mapRef={mapRef}
            onReset={handleReset}
            showReset={true}
          />
        </Container>
      </Wrapper>
    </>
  );
}

export default StateMap;
